"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { Menu, Search, X } from "lucide-react";
import { FormEvent, useState } from "react";
import { ModeToggle } from "@/components/ModeToggle";
import { BLOG_NAME, BLOG_TAGLINE } from "@/constants/constants";
import { Category } from "@/types/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type SiteHeaderProps = {
  categories: Category[];
};

export default function SiteHeader({ categories }: SiteHeaderProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    router.push(q ? `/posts?title=${encodeURIComponent(q)}` : "/posts");
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="border-b border-border bg-background/95 backdrop-blur-xl">
        <div className="blog-shell flex items-center justify-between gap-4 py-4">
          <Link href="/" className="flex min-w-0 items-center gap-3">
            <Image
              src="/blog-logo.png"
              alt={BLOG_NAME}
              width={44}
              height={44}
              className="size-10 shrink-0 brightness-0 dark:brightness-100 sm:size-11"
              priority
            />
            <span className="min-w-0">
              <span className="block font-[family-name:var(--font-display)] text-2xl font-bold tracking-tight sm:text-3xl">
                {BLOG_NAME}
              </span>
              <span className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                {BLOG_TAGLINE}
              </span>
            </span>
          </Link>

          <form onSubmit={onSearch} className="hidden max-w-sm flex-1 items-center gap-2 md:flex">
            <div className="relative w-full">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search posts…"
                className="h-9 pl-9"
                aria-label="Search posts"
              />
            </div>
            <Button type="submit" size="sm">
              Search
            </Button>
          </form>

          <div className="flex items-center gap-2">
            <Link
              href="/portfolio"
              className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-primary lg:inline"
            >
              Portfolio
            </Link>
            <ModeToggle />
            <Button
              variant="outline"
              size="icon"
              className="md:hidden"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </Button>
          </div>
        </div>
      </div>

      <nav
        className="border-b border-white/10"
        style={{ background: "var(--nav)", color: "var(--nav-foreground)" }}
      >
        <div className="blog-shell">
          <ul className="flex items-center gap-1 overflow-x-auto py-0 scrollbar-none">
            <li>
              <Link
                href="/"
                className={`inline-flex h-11 items-center px-3 text-sm font-medium transition-colors ${
                  pathname === "/"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-white/10"
                }`}
              >
                Home
              </Link>
            </li>
            {categories.map((category) => {
              const active = pathname === `/category/${category.slug}`;
              return (
                <li key={category.slug}>
                  <Link
                    href={`/category/${category.slug}`}
                    className={`inline-flex h-11 items-center whitespace-nowrap px-3 text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-white/10"
                    }`}
                  >
                    {category.name}
                  </Link>
                </li>
              );
            })}
            <li>
              <Link
                href="/posts"
                className={`inline-flex h-11 items-center px-3 text-sm font-medium transition-colors ${
                  pathname.startsWith("/posts")
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-white/10"
                }`}
              >
                All Posts
              </Link>
            </li>
            <li>
              <Link
                href="/about"
                className={`inline-flex h-11 items-center px-3 text-sm font-medium transition-colors ${
                  pathname === "/about"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-white/10"
                }`}
              >
                About
              </Link>
            </li>
            <li>
              <Link
                href="/contact"
                className={`inline-flex h-11 items-center px-3 text-sm font-medium transition-colors ${
                  pathname === "/contact"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-white/10"
                }`}
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {open && (
        <div className="space-y-4 border-b border-border bg-background px-4 py-4 md:hidden">
          <form onSubmit={onSearch} className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search posts…"
            />
            <Button type="submit">Go</Button>
          </form>
          <div className="flex flex-col gap-2 text-sm">
            <Link href="/posts" onClick={() => setOpen(false)}>
              All Posts
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                href={`/category/${c.slug}`}
                onClick={() => setOpen(false)}
              >
                {c.name}
              </Link>
            ))}
            <Link href="/about" onClick={() => setOpen(false)}>
              About
            </Link>
            <Link href="/contact" onClick={() => setOpen(false)}>
              Contact
            </Link>
            <Link href="/portfolio" onClick={() => setOpen(false)}>
              Portfolio
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
